/**
 * ProviderOrderServices - Serviços do negócio no app do prestador (BR-OS-046..048).
 *
 * Usa somente a Edge Function `provider-portal` (ações services.*) e os componentes
 * sem sessão da O.S. (janela "Serviços executados" e "Adicionar serviço").
 *
 * RELEASE: 2026-10-17 v1.3.1 - Contador "N de M realizados" moved to the same line as the section title (pill, theme tokens).
 * RELEASE: 2026-10-17 v1.3.0 - Lista compacta com divisórias sutis, situação sem selos e indicadores preservados.
 * RELEASE: 2026-10-17 v1.2.1 - Seção movida para antes da timeline (Andamentos) e margem extra removida (espaçamento vem do container).
 * RELEASE: 2026-10-17 v1.2.0 - Cartões com bolinha ✓/✗/pendente, quantidade, selo Adicional e contador.
 * RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, ClipboardList, Clock, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PROVIDER_DASHBOARD_CARD } from '@/components/dashboard/shared/dashboardCardStyles';
import { ServiceOrderFieldServiceDialog } from '@/components/service-orders/ServiceOrderFieldServiceDialog';
import { serviceStatusVisual, type ServiceOrderServicesData, type ServiceVisualTone } from '@/lib/serviceOrders/serviceItems';

/** Cores por situação (tokens do tema). */
const TONE: Record<ServiceVisualTone, { dot: string; label: string; Icon: typeof Check }> = {
  success: { dot: 'bg-success/10 text-success', label: 'text-success', Icon: Check },
  danger: { dot: 'bg-destructive/10 text-destructive', label: 'text-destructive', Icon: X },
  warning: { dot: 'bg-warning/10 text-warning', label: 'text-warning', Icon: Clock },
  pending: { dot: 'bg-muted text-muted-foreground', label: 'text-muted-foreground', Icon: Clock },
};

type Caller = <T>(action: string, payload?: Record<string, unknown>) => Promise<T>;

/** Chave compartilhada com o Concluir (OrderActions). */
export const providerServicesKey = (orderId: string) => ['provider-portal', 'services', orderId];

export function useProviderOrderServices(call: Caller, orderId: string) {
  return useQuery({
    queryKey: providerServicesKey(orderId),
    queryFn: () => call<ServiceOrderServicesData>('services.list', { id: orderId }),
  });
}

export function ProviderOrderServices({ call, orderId, canAdd }: { call: Caller; orderId: string; canAdd: boolean }) {
  const qc = useQueryClient();
  const query = useProviderOrderServices(call, orderId);
  const [addOpen, setAddOpen] = useState(false);
  const products = useQuery({
    queryKey: ['provider-portal', 'service-products'],
    enabled: addOpen,
    queryFn: async () => (await call<{ items: { id: string; name: string; sku: string | null }[] }>('services.products', { id: orderId })).items,
  });
  const add = useMutation({
    mutationFn: (a: { productId: string; quantity: number; notes: string }) =>
      call('services.add', { id: orderId, product_id: a.productId, quantity: a.quantity, notes: a.notes.trim() || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: providerServicesKey(orderId) }); setAddOpen(false); toast.success('Serviço enviado para análise'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = query.data?.items ?? [];
  if (!items.length && !canAdd) return null;

  return (
    <section className={cn(PROVIDER_DASHBOARD_CARD, 'border p-4')}>
      {/* v1.3.1: contador de progresso na mesma linha do título (padrão das demais seções). */}
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <ClipboardList className="h-4 w-4 shrink-0 text-primary" />
          <h3 className="shrink-0 text-sm font-semibold">Serviços</h3>
          {items.length > 0 && <span className="truncate rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{items.filter((i) => i.execution_status === 'done').length} de {items.length} realizados</span>}
        </div>
        {canAdd && <Button size="sm" variant="ghost" className="shrink-0 text-primary" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-3.5 w-3.5" />Adicionar</Button>}
      </div>
      {/* v1.1.0: falha de leitura não se passa por lista vazia */}
      {query.isError ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-destructive">Não foi possível carregar os serviços.</p>
          <Button size="sm" variant="outline" onClick={() => query.refetch()}>Tentar novamente</Button>
        </div>
      ) : query.isLoading ? <p className="text-xs text-muted-foreground">Carregando serviços…</p>
      : items.length === 0 ? <p className="text-xs text-muted-foreground">Nenhum serviço informado.</p> : (
        <ul className="divide-y divide-border/60">
          {items.map((i) => {
            const v = serviceStatusVisual(i); const t = TONE[v.tone];
            return (
              <li key={i.id} className="flex gap-3 py-3.5 last:pb-0">
                {/* Indicador discreto mantém a identificação sem depender apenas da cor. */}
                <span className={cn('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full', t.dot)} aria-label={v.label}>
                  <t.Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 break-words text-sm font-medium leading-snug">{i.product_name}</p>
                    <span className="shrink-0 text-xs leading-5 text-muted-foreground" aria-label={`Quantidade: ${Number(i.quantity)}`}>× {Number(i.quantity)}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                    <span className={cn('font-medium', t.label)}>{v.label}</span>
                    {i.origin === 'field' && <span className="text-muted-foreground">· Adicional</span>}
                  </div>
                  {/* Motivo e observação seguem a hierarquia secundária, sem alertas decorativos. */}
                  {i.not_done_reason && <p className="mt-2 break-words text-xs leading-relaxed text-muted-foreground"><span className="font-medium text-foreground">Motivo: </span>{i.not_done_reason}</p>}
                  {/* BR-OS-054: orientação do operador (somente leitura) */}
                  {i.operator_notes && <p className="mt-2 break-words text-xs leading-relaxed text-primary"><span className="font-medium">Orientação: </span>{i.operator_notes}</p>}
                  {i.notes && <p className="mt-2 break-words text-xs leading-relaxed text-muted-foreground"><span className="font-medium text-foreground">Observação: </span>{i.notes}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ServiceOrderFieldServiceDialog open={addOpen} onOpenChange={setAddOpen} products={products.data ?? []} loading={products.isLoading}
        saving={add.isPending} onConfirm={(a) => add.mutate(a)} />
    </section>
  );
}
